import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';

const CostSummary = ({ uploads, onConfirm, isProcessing = false }) => {
  const { t } = useTranslation('cost');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricingData, setPricingData] = useState(null);

  // Helper function to format currency
  const formatPrice = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  // Helper function to check if an upload has theme and actions
  const isFullyConfigured = (upload) => {
    return upload.imageTheme && upload.actions && upload.actions.length > 0;
  };

  // Get configured and unconfigured uploads
  const getUploadStats = () => {
    const configured = uploads.filter(isFullyConfigured);
    const unconfigured = uploads.filter(u => !isFullyConfigured(u));
    return { configured, unconfigured };
  };

  // Fetch the base pricing data
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await api.get('/api/pricing');
        setPricingData(res.data.data);
      } catch (error) {
        console.error('Error fetching pricing data:', error);
        setError('Failed to load pricing information');
      }
    };

    fetchPricing();
  }, []);

  // Calculate price whenever uploads or pricing data changes
  useEffect(() => {
    const calculatePrice = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (!pricingData || uploads.length === 0) {
          setCalculatedPrice(null);
          setLoading(false);
          return;
        }

        const { configured, unconfigured } = getUploadStats();
        
        // Calculate totals for configured uploads
        const configuredImages = configured.length;
        const configuredVariants = configured.reduce((total, upload) => 
          total + upload.actions.reduce((imgTotal, action) => imgTotal + (action.variety || 1), 0), 0);
        
        // Add default values for unconfigured uploads (1 variety each)
        const totalImages = configuredImages + unconfigured.length;
        const totalVariants = configuredVariants + unconfigured.length; // 1 variety per unconfigured image
        
        // The "effective" variants per image
        const effectiveVariantsPerImage = totalVariants / totalImages;

        // Get price calculation from API
        const response = await api.post('/api/pricing/calculate', {
          imageCount: totalImages,
          variantsPerImage: effectiveVariantsPerImage
        });

        if (response.data) {
          setCalculatedPrice(response.data);
        } else {
          setError('Invalid price calculation response');
        }
      } catch (error) {
        console.error('Error calculating price:', error);
        setError('Failed to calculate price');
      } finally {
        setLoading(false);
      }
    };

    calculatePrice();
  }, [uploads, pricingData]);

  const calculateTotalActions = () => {
    const { configured, unconfigured } = getUploadStats();
    const configuredActions = configured.reduce((total, upload) => {
      return total + upload.actions.reduce((actionTotal, action) => {
        return actionTotal + action.variety;
      }, 0);
    }, 0);
    return configuredActions + unconfigured.length; // Add 1 action per unconfigured upload
  };

  if (uploads.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[440px]">
        <h3 className="text-lg font-semibold mb-4">{t('summary.title')}</h3>
        <p className="text-gray-500">{t('summary.addImages')}</p>
      </div>
    );
  }

  const { configured, unconfigured } = getUploadStats();
  const totalActions = calculateTotalActions();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[440px]">
        <h3 className="text-lg font-semibold mb-4">{t('summary.title')}</h3>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !calculatedPrice) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[440px]">
        <h3 className="text-lg font-semibold mb-4">{t('summary.title')}</h3>
        <p className="text-red-500">{error || 'Unable to calculate price'}</p>
      </div>
    );
  }

  const originalTotal = Number(pricingData.basePrice) * uploads.length + 
    Number(pricingData.variantPrice) * (totalActions - uploads.length);

  // Calculate if there's an actual discount
  const hasDiscount = originalTotal > calculatedPrice.total;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[440px]">
      <h3 className="text-lg font-semibold mb-4">{t('summary.title')}</h3>
      <div className="space-y-2">
        <div className="flex">
          <span className="w-[60%]">{t('summary.photos')}:</span>
          <span className="w-[40%] text-right">{t('summary.photosCount', { count: uploads.length })}</span>
        </div>
        <div className="flex">
          <span className="w-[60%]">{t('summary.versions')}:</span>
          <span className="w-[40%] text-right">{t('summary.versionsCount', { count: totalActions })}</span>
        </div>

        {pricingData && (
          <div className="border-t pt-2 mt-2">
            {/* Base Prices Section */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">{t('pricing.breakdown')}</div>
              <div className="flex text-sm text-gray-600">
                <span className="w-[60%]">{t('pricing.baseAnimation')}:</span>
                <span className="w-[40%] text-right">{formatPrice(pricingData.basePrice, pricingData.currency)}</span>
              </div>
              <div className="flex text-sm text-gray-600">
                <span className="w-[60%]">{t('pricing.additionalAnimations')}:</span>
                <span className="w-[40%] text-right">{formatPrice(pricingData.variantPrice, pricingData.currency)}</span>
              </div>
            </div>

            {/* Applied Discounts Summary */}
            {calculatedPrice?.appliedDiscounts && Object.values(calculatedPrice.appliedDiscounts).some(amount => amount > 0) && (
              <div className="mb-4 bg-green-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-green-700 mb-2">{t('discounts.title')}</div>
                {calculatedPrice.appliedDiscounts.volumeDiscount > 0 && (
                  <div className="flex text-sm text-green-600">
                    <span className="w-[60%]">{t('discounts.volumeDiscount')}:</span>
                    <span className="w-[40%] text-right">{formatPrice(calculatedPrice.appliedDiscounts.volumeDiscount, pricingData.currency)}</span>
                  </div>
                )}
                {calculatedPrice.appliedDiscounts.variantDiscount > 0 && (
                  <div className="flex text-sm text-green-600">
                    <span className="w-[60%]">{t('discounts.bundleDiscount')}:</span>
                    <span className="w-[40%] text-right">{formatPrice(calculatedPrice.appliedDiscounts.variantDiscount, pricingData.currency)}</span>
                  </div>
                )}
                <div className="border-t border-green-200 mt-2 pt-2 flex text-sm font-medium text-green-700">
                  <span className="w-[60%]">{t('discounts.totalSavings')}:</span>
                  <span className="w-[40%] text-right">{formatPrice(originalTotal - calculatedPrice.total, pricingData.currency)}</span>
                </div>
              </div>
            )}

            {/* Final Total */}
            {calculatedPrice?.total && (
              <div className="border-t pt-4">
                <div className="text-lg font-semibold mb-2">{t('total.finalPrice')}</div>
                {hasDiscount && (
                  <div className="flex text-gray-600 mb-2">
                    <span className="w-[60%]">{t('total.originalTotal')}:</span>
                    <span className="w-[40%] text-right">{formatPrice(originalTotal, pricingData.currency)}</span>
                  </div>
                )}
                <div className="flex font-semibold text-lg text-blue-600">
                  <span className="w-[60%]">{hasDiscount ? t('total.discountedPrice') : t('total.totalPrice')}:</span>
                  <span className="w-[40%] text-right">{formatPrice(calculatedPrice.total, pricingData.currency)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CostSummary;